/* eslint-disable react/require-default-props */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import ErrorBoundary from '../../utils/ErrorBoundary';
import { handleFareItemInteraction } from './commonFns';
import { renderOneLabel } from '../../containers/FlightsRecommender/utils/utils';

import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Text from '../../elements/Text';
import Link from '../../elements/Link';
import Icon from '../../elements/Icon';
import FareItemOriginDestination from '../_commons/atoms/FareItemOriginDestination';
import FareItemOriginDestinationImage from '../_commons/atoms/FareItemOriginDestinationImage';
import FareItemDepartureInfo from '../_commons/atoms/FareItemDepartureInfo';
import FareItemReturnInfo from '../_commons/atoms/FareItemReturnInfo';
import FareItemJourneyType from '../_commons/atoms/FareItemJourneyType';
import FareItemTravelClass from '../_commons/atoms/FareItemTravelClass';
import FareItemTags from '../_commons/atoms/FareItemTags';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import FareItemCallToAction from '../_commons/atoms/FareItemCallToAction';
import FareItemLastSeen from '../_commons/atoms/FareItemLastSeen';
import Separator from '../_commons/atoms/Separator';

const FareItemMediumSizeGrouped = (props) => {
  const {
    data: { fares, info },
    isMobile,
    columns = [
      'FareItemDepartureReturnInfo',
      'FareItemJourneyTypeTravelClass',
      'FareItemTags',
      'FareItemPrice',
      'FareItemLastSeen',
      'FareItemCallToAction'
    ],
    showLocationInfo = true,
    showAirportRouteToCity = true,
    seeMoreBasicUrl,
    showDeeplinkOnRoute,
    siteEdition,
    trfxVariant = 'cici',
    dispatch,
    unifiedCityDescriptionRequired = false,
    cellRouteDetailsSettings = {},
    useFlightSearchVariant = false,
    showOriginDestinationImage = true
  } = props;

  const { direction, translate, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const labelButton = props?.fareItemCallToAction.showCallToActionTextMin
    ? translate('fare_call_to_action_label_min', 'Book')
    : translate('fare_call_to_action_label', 'Book now');

  const callToActionLabel = props?.showCallToAction ? labelButton : 'fare-card';

  const {
    handleCallToAction,
    handleClick,
    handleItemKeyDown,
    handleCallToActionKeyDown,
    hasInteraction
  } = handleFareItemInteraction(props, 'fareItemCallToAction', callToActionLabel);

  const cursorClass = hasInteraction ? 'fr-cursor-pointer' : '';

  const [tableClass, tableStyle] = useStyles(props?.tableSettings, { direction, theme });
  const [rowClass, rowStyle] = useStyles(props?.rowSettings, { direction, theme });
  const [cellClassRouteDetails, cellStyleRouteDetails] = useStyles(cellRouteDetailsSettings, {
    direction,
    theme
  });
  const [cellClass, cellStyle] = useStyles(props?.cellSettings, { direction, theme });

  const showInfoData = Boolean(info?.cityBlurb && info?.typicalWeather && info?.uniqueThingsToDo);

  const components = {
    FareItemDepartureReturnInfo: (data) => (
      <Box
        {...props?.fareItemDepartureReturn}
        key={`drinfo-${data.index}`}
        id={`fare-item-departure-return-info-${data.index}`}
      >
        {/* FareItemDepartureInfo */}
        {props?.showDepartureInfo && (
          <FareItemDepartureInfo {...props?.fareItemDepartureInfo} {...data} />
        )}
        {/* Separator */}
        {props?.showDepartureInfo &&
          props?.showReturnInfo &&
          data.formattedDepartureDate &&
          data.formattedReturnDate && <Separator floatLeft {...props?.separatorDatesProps} />}
        {/* FareItemReturnInfo */}
        {props?.showReturnInfo && <FareItemReturnInfo {...props?.fareItemReturnInfo} {...data} />}
      </Box>
    ),
    FareItemJourneyTypeTravelClass: (data) => (
      <Box display="flex" id={`fare-item-journey-type-travel-class-${data.index}`}>
        {/* FareItemTravelClass */}
        {props?.showTravelClass && (
          <FareItemTravelClass {...props?.fareItemTravelClass} {...data} />
        )}
        {/* Separator */}
        {props?.showTravelClass && <Separator {...props?.separatorJourneyTypeProps} floatLeft />}
        {props?.showJourneyType && (
          <FareItemJourneyType {...props?.fareItemJourneyType} {...data} />
        )}
      </Box>
    ),
    FareItemTags: (data) => (
      <Box {...props?.fareItemTagsContainer}>
        <FareItemTags id={`fare-item-tags-${data.index}`} {...props?.fareItemTags} {...data} />
      </Box>
    ),
    FareItemPrice: (data) => (
      <FareItemPrice id={`fare-item-price-${data.index}`} {...props.fareItemPrice} {...data} />
    ),
    FareItemLastSeen: (data) => (
      <FareItemLastSeen
        id={`fare-item-last-seen-${data.index}`}
        {...props?.fareItemLastSeen}
        {...data}
      />
    ),
    FareItemCallToAction: (data) => (
      <FareItemCallToAction
        {...props?.fareItemCallToAction}
        {...data}
        availableAtoms={columns}
        onClick={(evt) => handleCallToAction(evt, callToActionLabel, { data })}
        onKeyDown={(evt) => handleCallToActionKeyDown(evt, callToActionLabel, { data })}
        enabledDataForSR={props?.enabledDataForSR}
      />
    ),
    FareItemPriceDetails: (data) => (
      <Box className={`fare-item-price-details`} {...props?.fareItemPriceDetailsContainer}>
        {props?.showFareItemPrice && (
          <FareItemPrice id={`fare-item-price-${data.index}`} {...props.fareItemPrice} {...data} />
        )}
        {props?.showLastSeenDetails && components['FareItemLastSeen'](data)}
      </Box>
    ),
    FareItemDetails: (data) => {
      let airlineName = '';
      if (props?.showAirlineName) {
        const airlinesNames = props?.fareItemAirline?.airlinesNames;
        airlinesNames.forEach((item) => {
          if (item.airlineCode === data.airlineCode) {
            airlineName = translate(item.labelKey, item.defaultValue);
            return;
          }
        });
      }
      return (
        <Box className={`fare-item-details`} {...props?.fareItemDetails?.containerDetails}>
          {props?.showAirlineName && (
            <Box className={`fare-item-airline`} {...props?.fareItemAirline?.containerAirlineName}>
              <Text {...props?.fareItemAirline?.airlineNameStyles}>{airlineName}</Text>
            </Box>
          )}
          {props?.showSeparator && <Separator {...props?.fareItemSeparator} />}
          {components['FareItemJourneyTypeTravelClass'](data)}
        </Box>
      );
    }
  };

  const renderImage = () => {
    return (
      <Box flex={props?.fareImageItem?.grow} {...props?.fareImageContainer}>
        <Box height="100" {...props?.fareImageItem}>
          <FareItemOriginDestinationImage
            {...info?.route}
            {...props?.fareItemOriginDestinationImage}
          >
            {useFlightSearchVariant && (
              <Box {...props?.fareImageContent}>
                {info?.distanceToCity?.value !== undefined && info?.distanceToCity?.value !== 0 && (
                  <Text floatRight {...props?.distanceToCity}>
                    {props?.distanceToCity?.showIcon && (
                      <Icon icon={'marker'} size={20} {...props?.distanceToCity?.iconSettings} />
                    )}
                    {info?.distanceToCity?.formattedText}
                  </Text>
                )}
              </Box>
            )}
          </FareItemOriginDestinationImage>
        </Box>
      </Box>
    );
  };
  const handleClickLink = (fare) => {
    dispatch({ type: 'CLICKED_ROUTE_DEEPLINK', payload: { fare } });
  };

  const renderSummary = () => {
    return (
      <>
        <Row gutters={true} {...props?.rowContainerSettings}>
          <Col lg={12}>
            <Box {...props?.sumaryHeaderSettings}>
              {showDeeplinkOnRoute && seeMoreBasicUrl && seeMoreBasicUrl !== '' ? (
                <Link
                  className="fare-item-origin-destination-link"
                  color="transparent"
                  onClick={() => handleClickLink(info)}
                  target="_blank"
                  href={`${seeMoreBasicUrl}/redirect?edition=${siteEdition}&orig=${fares[0]?.origin}&dest=${fares[0]?.destination}&tc=${trfxVariant}`}
                  {...props?.fareItemOriginDestinationLink}
                >
                  {renderOneLabel({
                    direction,
                    translate,
                    ...info?.route,
                    ...props?.fareItemOriginDestination
                  })}
                </Link>
              ) : (
                <FareItemOriginDestination
                  {...info?.route}
                  {...props?.fareItemOriginDestinationTitle}
                />
              )}
              {!useFlightSearchVariant &&
                info?.distanceToCity?.value !== undefined &&
                info?.distanceToCity?.value !== 0 &&
                showLocationInfo && (
                  <Text floatRight {...props?.distanceToCity}>
                    {props?.distanceToCity?.showIcon && (
                      <Icon icon={'marker'} size={20} {...props?.distanceToCity?.iconSettings} />
                    )}
                    {`${info?.distanceToCity?.value} ${info?.distanceToCity?.unit} ${translate(
                      'cmp-fare-item-grouped-from',
                      'from'
                    )} ${info?.route?.destinationCity} ${translate(
                      'cmp-fare-item-grouped-distance-to-city',
                      'to city center'
                    )}`}
                  </Text>
                )}
            </Box>
            {showLocationInfo && showInfoData && (
              <Text {...props?.cityBlurb}>{info?.cityBlurb}</Text>
            )}
          </Col>
          {showLocationInfo && showInfoData && (
            <>
              <Col lg={6} {...props?.uniqueThingsToDoCol}>
                <Text {...props?.uniqueThingsToDoColLabel}>
                  {translate('cmp-fare-item-grouped-unique-things', 'Unique Things to Do')}
                </Text>
                <Text {...props?.uniqueThingsToDoValue}>{info?.uniqueThingsToDo}</Text>
              </Col>
              <Col lg={6} {...props?.typicalWeatherCol}>
                <Text {...props?.typicalWeatherLabel}>
                  {translate('cmp-fare-item-grouped-typical-weather', 'Typical Weather')}
                </Text>
                <Text {...props?.typicalWeatherValue}>{info?.typicalWeather}</Text>
              </Col>
            </>
          )}
        </Row>
        {useFlightSearchVariant && !isMobile && (
          <Row {...props?.fareItemsWrapper}>{renderFaresItems()}</Row>
        )}
      </>
    );
  };
  const renderUnifiedSummary = () => {
    return (
      <>
        <Row gutters={true} {...props?.rowContainerSettings}>
          <Col lg={12}>
            <Box
              className="fare-item-origin-destination-header"
              color="transparent"
              {...props?.fareItemOriginDestinationHeader}
            >
              {`${translate('cmp-fare-item-grouped-title', 'Flights to')} ${info?.cityName}`}
            </Box>
            {showLocationInfo && showInfoData && (
              <Text {...props?.cityBlurb}>{info?.cityBlurb}</Text>
            )}
          </Col>
          {showLocationInfo && showInfoData && (
            <>
              <Col lg={6} {...props?.uniqueThingsToDoCol}>
                <Text {...props?.uniqueThingsToDoColLabel}>
                  {translate('cmp-fare-item-grouped-unique-things', 'Unique Things to Do')}
                </Text>
                <Text {...props?.uniqueThingsToDoValue}>{info?.uniqueThingsToDo}</Text>
              </Col>
              <Col lg={6} {...props?.typicalWeatherCol}>
                <Text {...props?.typicalWeatherLabel}>
                  {translate('cmp-fare-item-grouped-typical-weather', 'Typical Weather')}
                </Text>
                <Text {...props?.typicalWeatherValue}>{info?.typicalWeather}</Text>
              </Col>
            </>
          )}
        </Row>
        {useFlightSearchVariant && !isMobile && (
          <Row {...props?.fareItemsWrapper}>{renderFaresItems()}</Row>
        )}
      </>
    );
  };

  const renderRouteDetails = (info) => {
    return (
      <tr>
        <td colspan="6" style={cellStyleRouteDetails} className={cellClassRouteDetails}>
          <Row gutters={true}>
            <Col lg={12} {...props?.colRouteDetailsSettings}>
              <Box {...props?.sumaryHeaderSettings}>
                {showDeeplinkOnRoute && seeMoreBasicUrl && seeMoreBasicUrl !== '' ? (
                  <Link
                    className="fare-item-origin-destination-link"
                    color="transparent"
                    onClick={() => handleClickLink(info)}
                    target="_blank"
                    href={`${seeMoreBasicUrl}/redirect?edition=${siteEdition}&orig=${info?.route?.origin}&dest=${info?.route?.destination}&tc=${trfxVariant}`}
                    {...props?.fareItemOriginDestinationLink}
                  >
                    {renderOneLabel({
                      direction,
                      translate,
                      ...info?.route,
                      ...props?.fareItemOriginDestination
                    })}
                  </Link>
                ) : (
                  <FareItemOriginDestination
                    {...info?.route}
                    {...props?.fareItemOriginDestinationTitle}
                  />
                )}
                {!useFlightSearchVariant &&
                  info?.distanceToCity?.value !== undefined &&
                  info?.distanceToCity?.value !== 0 &&
                  showLocationInfo && (
                    <Text {...props?.distanceToCityUnified}>
                      {props?.distanceToCityUnified?.showIcon && (
                        <Icon
                          icon={'marker'}
                          size={20}
                          {...props?.distanceToCityUnified?.iconSettings}
                        />
                      )}
                      {`${info?.distanceToCity?.value} ${info?.distanceToCity?.unit} ${translate(
                        'cmp-fare-item-grouped-from',
                        'from'
                      )} ${info?.route?.destinationCity} ${translate(
                        'cmp-fare-item-grouped-distance-to-city',
                        'to city center'
                      )}`}
                    </Text>
                  )}
              </Box>
              {showAirportRouteToCity && info?.airportRouteToCity && (
                <Text {...props?.airportRouteToCitySettings}>{info?.airportRouteToCity}</Text>
              )}
            </Col>
          </Row>
        </td>
      </tr>
    );
  };

  const renderUnifiedFaresItems = () => {
    return fares.map((faresPerRoute, index) => {
      return (
        <React.Fragment key={index}>
          {renderRouteDetails(faresPerRoute.info)}
          {faresPerRoute.fares.map((item) => (
            <tr className={rowClass} style={rowStyle} onClick={() => handleClick({ data: item })}>
              {columns.map((column) => {
                const Component = components[column];
                return (
                  <td
                    key={`${column}-${item.datacoreId}`}
                    className={`fr-fare-item-medium-size-grouped-${column.toLowerCase()} ${cellClass}`}
                    style={cellStyle}
                  >
                    <Component {...item} />
                  </td>
                );
              })}
            </tr>
          ))}
        </React.Fragment>
      );
    });
  };

  const renderRouteDetailsMobile = (info) => {
    return (
      <Row gutters={true}>
        <Col lg={12}>
          <Box {...props?.containerRouteDetailsSettings}>
            {showDeeplinkOnRoute && seeMoreBasicUrl && seeMoreBasicUrl !== '' ? (
              <Link
                className="fare-item-origin-destination-link"
                color="transparent"
                onClick={() => handleClickLink(info)}
                target="_blank"
                href={`${seeMoreBasicUrl}/redirect?edition=${siteEdition}&orig=${info?.route?.origin}&dest=${info?.route?.destination}&tc=${trfxVariant}`}
                {...props?.fareItemOriginDestinationLink}
              >
                {renderOneLabel({
                  direction,
                  translate,
                  ...info?.route,
                  ...props?.fareItemOriginDestination
                })}
              </Link>
            ) : (
              <FareItemOriginDestination
                {...info?.route}
                {...props?.fareItemOriginDestinationTitle}
              />
            )}
            {!useFlightSearchVariant &&
              info?.distanceToCity?.value !== undefined &&
              info?.distanceToCity?.value !== 0 &&
              showLocationInfo && (
                <Text {...props?.distanceToCityUnified}>
                  {props?.distanceToCityUnified?.showIcon && (
                    <Icon
                      icon={'marker'}
                      size={20}
                      {...props?.distanceToCityUnified?.iconSettings}
                    />
                  )}
                  {`${info?.distanceToCity?.value} ${info?.distanceToCity?.unit} ${translate(
                    'cmp-fare-item-grouped-from',
                    'from'
                  )} ${info?.route?.destinationCity} ${translate(
                    'cmp-fare-item-grouped-distance-to-city',
                    'to city center'
                  )}`}
                </Text>
              )}
            {showAirportRouteToCity && info?.airportRouteToCity && (
              <Text {...props?.airportRouteToCitySettings}>{info?.airportRouteToCity}</Text>
            )}
          </Box>
        </Col>
      </Row>
    );
  };

  const renderUnifiedFaresItemsMobile = () => {
    return fares.map((faresPerRoute, index) => {
      return (
        <React.Fragment key={index}>
          {renderRouteDetailsMobile(faresPerRoute.info)}
          {faresPerRoute.fares.map((item) => {
            return (
              <Box
                className={`fare-item-rows ${cursorClass}`}
                {...props?.fareItemWrapper}
                onClick={() => handleClick({ data: item })}
                onFocus={props?.onFocus}
                onKeyDown={() => handleItemKeyDown({ data: item })}
              >
                {columns.map((column) => {
                  const Component = components[column];
                  return <Component key={`${column}-${item.datacoreId}`} {...item} />;
                })}
              </Box>
            );
          })}
        </React.Fragment>
      );
    });
  };

  const renderFaresItems = () => {
    if (isMobile) {
      if (unifiedCityDescriptionRequired) {
        return renderUnifiedFaresItemsMobile();
      }
      return (
        <Box>
          {fares.map((item) => {
            return (
              <Box
                className={`fare-item-rows ${cursorClass}`}
                {...props?.fareItemWrapper}
                onClick={() => handleClick({ data: item })}
                onFocus={props?.onFocus}
                onKeyDown={() => handleItemKeyDown({ data: item })}
              >
                {columns.map((column) => {
                  const Component = components[column];
                  return <Component key={`${column}-${item.datacoreId}`} {...item} />;
                })}
              </Box>
            );
          })}
        </Box>
      );
    } else {
      return (
        <table className={tableClass} style={tableStyle}>
          {unifiedCityDescriptionRequired && renderUnifiedFaresItems()}
          {!unifiedCityDescriptionRequired &&
            fares.map((item) => {
              return (
                <tr
                  className={rowClass}
                  style={rowStyle}
                  onClick={() => handleClick({ data: item })}
                >
                  {columns.map((column) => {
                    const Component = components[column];
                    return (
                      <td
                        key={`${column}-${item.datacoreId}`}
                        className={`fr-fare-item-medium-size-grouped-${column.toLowerCase()} ${cellClass}`}
                        style={cellStyle}
                      >
                        <Component {...item} />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </table>
      );
    }
  };

  return (
    <ErrorBoundary>
      <Box className={className} style={style}>
        <Row {...props?.containerRow}>
          {showLocationInfo && showInfoData && showOriginDestinationImage ? (
            <Col {...props?.imageContainer}>{renderImage()}</Col>
          ) : null}
          <Col {...props?.summaryContainer}>
            {unifiedCityDescriptionRequired ? renderUnifiedSummary() : renderSummary()}
          </Col>
        </Row>
        <Row>
          {(!useFlightSearchVariant || (useFlightSearchVariant && isMobile)) && (
            <Col>{renderFaresItems()}</Col>
          )}
        </Row>
      </Box>
    </ErrorBoundary>
  );
};

FareItemMediumSizeGrouped.propTypes = {
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * OnClick event
   */
  onFocus: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * Props of fareItem
   */
  fareItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemCallToAction
   */
  fareItemCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemCallToAction container
   */
  fareItemCallToActionContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemDepartureInfo
   */
  fareItemDepartureInfo: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemTripDuration
   */
  fareItemTripDuration: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemReturnInfo
   */
  fareItemReturnInfo: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemDeparture and fareItemReturn together
   */
  fareItemDepartureReturn: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemJourneyType and fareItemTravelClass together
   */
  fareItemJourneyTypeTravelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemOriginDestinationImage
   */
  fareItemOriginDestinationImage: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemJourneyType
   */
  fareItemJourneyType: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemTravelClass
   */
  fareItemTravelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemOriginDestination
   */
  fareItemOriginDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemPrice
   */
  fareItemPrice: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemMiles
   */
  fareItemMiles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemRoute
   */
  fareItemRoute: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of tileBodyItem
   */
  fareBodyItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of tileFooterItem
   */
  fareFooterItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareImageItem
   */
  fareImageItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareImageContainer
   */
  fareImageContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of tileContentItem
   */
  fareContentItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemAirline
   */
  fareItemAirline: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemDisclaimer
   */
  fareItemDisclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of fareItemLastSeen
   */
  fareItemLastSeen: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of separator
   */
  separatorDatesProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of separator
   */
  separatorJourneyTypeProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Turn off or on the journeyType section
   */
  showOriginDestinationImage: PropTypes.bool,
  /**
   * Turn off or on the journeyType section
   */
  showJourneyType: PropTypes.bool,
  /**
   * Turn off or on the showDurationTrip section
   */
  showDurationTrip: PropTypes.bool,
  /**
   * Turn off or on the travelClass section
   */
  showTravelClass: PropTypes.bool,
  /**
   * Turn off or on the DepartureInfo section
   */
  showDepartureInfo: PropTypes.bool,
  /**
   * Turn off or on the ReturnInfo section
   */
  showReturnInfo: PropTypes.bool,
  /**
   * Turn off or on the LastSeen section
   */
  showLastSeen: PropTypes.bool,
  /**
   * Turn off or on the Route section
   */
  showRoute: PropTypes.bool,
  /**
   * Turn off or on the Price section
   */
  showPrice: PropTypes.bool,
  /**
   * Turn off or on the Miles section
   */
  showMiles: PropTypes.bool,
  /**
   * Turn off or on the Fare Disclaimer section
   */
  showFareDisclaimer: PropTypes.bool,
  /**
   * Turn off or on the Airline section
   */
  showAirline: PropTypes.bool,
  /**
   * Turn off or on the Call To Action section
   */
  showCallToAction: PropTypes.bool,
  /**
   * Show sale atom
   */
  showSale: PropTypes.bool,
  /**
   * Show FareItemRoute before FareItemDepartureInfo
   */
  showFareItemRouteBeforeDepartureInfo: PropTypes.bool,
  /**
   * Show FareItemLastSeen before FareItemPrice
   */
  showLastSeenBeforePrice: PropTypes.bool,
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * ref
   */
  focusReferences: PropTypes.func,
  /**
   * Turn off or on tabIndex in FareItem
   */
  focusable: PropTypes.bool,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interactionHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * Data test ID of FareItem
   */
  dataTestId: PropTypes.string,
  /**
   * Define class to focus
   */
  focusClass: PropTypes.string
};

FareItemMediumSizeGrouped.componentName = 'FareItemMediumSizeGrouped';

export default FareItemMediumSizeGrouped;
